import { supabase } from '@/lib/supabase'

/**
 * Inbox message-body search (CP1 backend — data helper only, no UI).
 *
 * Runs a plain RLS-relying query against messages: substring match on content
 * via ILIKE, backed by the messages_content_search trigram GIN index. There is
 * NO RPC and NO SECURITY DEFINER — the query inherits the existing messages
 * workspace_isolation policy (workspace_id = get_user_workspace_id()), so a
 * caller only ever matches messages in their own workspace. Search is not a
 * data-leak surface.
 *
 * All message types are searchable (inbound, outbound, note) — notes are real
 * workspace content and should be findable. messages has no soft-delete column,
 * so there is no deleted_at filter (unlike team-chat search).
 *
 * CP2 wires the UI (a search surface + results + open-the-conversation).
 * Mirrors src/lib/searchTeamMessages.ts.
 */

export interface InboxMessageSearchResult {
  id: string
  conversation_id: string
  workspace_id: string
  content: string
  type: string | null
  sender_name: string | null
  created_at: string | null
}

/** Max rows returned per search. */
const SEARCH_LIMIT = 50

/**
 * Escape LIKE/ILIKE wildcards so a literal `%` or `_` is matched as itself.
 * Postgres LIKE uses backslash as the default escape character, so `\%`, `\_`,
 * and `\\` are honored without an explicit ESCAPE clause. Single pass — no
 * double-escaping. (Same escaping as searchTeamMessages.)
 */
function escapeLike(input: string): string {
  return input.replace(/[\\%_]/g, (c) => `\\${c}`)
}

/**
 * Search the caller's inbox messages for `query` (case-insensitive substring).
 * Returns up to 50 most-recent matches, newest first. RLS-scoped to the caller's
 * workspace. Empty/whitespace-only queries short-circuit to [] — no '%%' scan.
 */
export async function searchInboxMessages(
  query: string,
): Promise<InboxMessageSearchResult[]> {
  const trimmed = query.trim()
  if (!trimmed) return []

  const pattern = `%${escapeLike(trimmed)}%`

  const { data, error } = await supabase
    .from('messages')
    .select('id, conversation_id, workspace_id, content, type, sender_name, created_at')
    .ilike('content', pattern)
    .order('created_at', { ascending: false })
    .limit(SEARCH_LIMIT)

  if (error) {
    console.error('searchInboxMessages failed', error)
    throw error
  }

  return (data ?? []) as InboxMessageSearchResult[]
}
